import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check, X, Megaphone, Mail, Calendar, Newspaper, MessageSquare, Tag, Users, Building2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICON = {
  newsletter: Mail,
  announcement: Megaphone,
  event: Calendar,
  blog: Newspaper,
  message: MessageSquare,
  membership: Tag,
  community: Users,
  business: Building2,
  info: Bell,
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en', { month: 'short', day: 'numeric' });
};

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const pollRef = useRef(null);

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=20`, { withCredentials: true });
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch {
      /* silent — bell is best-effort */
    }
  };

  useEffect(() => {
    fetchAll();
    // Poll unread count every 60s
    pollRef.current = setInterval(fetchAll, 60000);
    return () => clearInterval(pollRef.current);
  }, []);

  // Click-outside to close (mouse + touch for mobile)
  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('touchstart', onDocClick);
    }
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      await fetchAll();
      setLoading(false);
    }
  };

  const markRead = async (id) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, read: true } : it)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((it) => ({ ...it, read: true })));
    setUnread(0);
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
    } catch { /* ignore */ }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-100 u-btn"
        aria-label="Notifications"
        data-testid="notifications-bell"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E63946] text-white text-[10px] font-bold flex items-center justify-center shadow-sm u-pulse"
            data-testid="notifications-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.2, 0, 0, 1] }}
            className="absolute right-0 top-full mt-2 w-[min(360px,calc(100vw-24px))] bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden"
            data-testid="notifications-dropdown"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div>
                <h4 className="text-[15px] font-bold text-[#002249] leading-tight">Notifications</h4>
                <p className="text-[11px] text-slate-500 mt-0.5">{unread} unread</p>
              </div>
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[11.5px] text-[#E63946] hover:text-[#c02a35] font-semibold"
                  data-testid="notifications-read-all"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-[420px] overflow-y-auto">
              {loading ? (
                <div className="py-10 text-center text-[13px] text-slate-400">Loading…</div>
              ) : items.length === 0 ? (
                <div className="py-12 text-center px-4">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                    <Bell className="w-5 h-5 text-slate-400" />
                  </div>
                  <p className="text-[13px] font-semibold text-slate-700">All caught up</p>
                  <p className="text-[11.5px] text-slate-500 mt-0.5">You'll see announcements, updates and newsletters here.</p>
                </div>
              ) : (
                <ul>
                  {items.map((n) => {
                    const Icon = TYPE_ICON[n.type] || Bell;
                    const target = n.link || '/notifications';
                    const linkEl = (
                      <div
                        className={`flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer ${
                          n.read ? 'bg-white' : 'bg-[#002249]/[0.04]'
                        } hover:bg-slate-50`}
                        onClick={() => { if (!n.read) markRead(n.id); setOpen(false); }}
                        data-testid={`notification-item-${n.id}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                          n.read ? 'bg-slate-100 text-slate-500' : 'bg-[#002249] text-white'
                        }`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-[13px] ${n.read ? 'font-medium text-slate-700' : 'font-semibold text-[#050505]'} leading-snug`}>
                            {n.title}
                          </p>
                          {n.body && (
                            <p className="text-[12px] text-slate-500 leading-snug mt-0.5 line-clamp-2">{n.body}</p>
                          )}
                          <p className="text-[10.5px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                        </div>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-[#E63946] flex-shrink-0 mt-1" />}
                      </div>
                    );
                    return (
                      <li key={n.id} className="border-b border-slate-100 last:border-0">
                        <Link to={target} style={{ display: 'block' }}>{linkEl}</Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Footer — view all */}
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 text-center text-[12.5px] font-semibold text-[#002249] hover:bg-slate-50 border-t border-slate-100"
              data-testid="notifications-view-all"
            >
              View all notifications →
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
